import CategoryTag from "@/components/base/CategoryTag";
import { CONTENT_TYPE_META } from "@/config/brand";
import type { PostCard } from "@/types/content";

interface ArticleCardProps {
  post: PostCard;
  aspectRatio?: "16/10" | "4/3";
  padding?: "normal" | "compact";
  titleClassName?: string;
  surface?: "white" | "alt";
  tags?: React.ReactNode;
  imageOverlay?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  showCta?: boolean;
}

export default function ArticleCard({
  post,
  aspectRatio = "16/10",
  padding = "normal",
  titleClassName,
  surface = "white",
  tags,
  imageOverlay,
  footer,
  className = "",
  showCta = true,
}: ArticleCardProps) {
  const padClass = padding === "compact" ? "p-4" : "p-4 md:p-5";
  const defaultTitle = padding === "compact" ? "text-sm" : "text-sm md:text-base";
  const surfaceClass = surface === "alt" ? "bg-surface-alt" : "bg-white";

  const ctMeta = post.contentType ? CONTENT_TYPE_META[post.contentType] : null;

  return (
    <a
      href={`/articles/${post.slug}`}
      className={`group block rounded-xl overflow-hidden card-shadow hover:card-shadow-hover transition-all duration-300 ${surfaceClass} ${className}`}
    >
      <div
        className={`relative ${aspectRatio === "4/3" ? "aspect-[4/3]" : "aspect-[16/10]"} overflow-hidden`}
      >
        <img
          loading="lazy"
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <CategoryTag label={post.category} variant="colored" />
          {ctMeta && (
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ctMeta.badgeClass}`}>
              {ctMeta.shortLabel}
            </span>
          )}
          {tags}
        </div>
        {imageOverlay}
      </div>
      <div className={padClass}>
        <h3
          className={`${titleClassName || defaultTitle} font-semibold text-slate-800 group-hover:text-primary transition-colors leading-snug line-clamp-2 mb-2`}
        >
          {post.title}
        </h3>
        {footer}
        {showCta && (
          <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary">
            閱讀全文
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </div>
        )}
      </div>
    </a>
  );
}